/**
 * #287 — the map settings a superuser sets for the whole company. They arrive with the map config
 * (`/api/me/map-config/` → `map_settings`), partial or missing on an older API, so everything is
 * merged over the defaults here. The defaults are the look built through item 286.
 */
import type { MapSettings, OfflineMark, PinColour, PinShape, PinSize, MapStyle, WedgeColour } from '@/types'
import { DEFAULT_FOV, DEFAULT_RANGE_M, type WedgeDefaults } from '@/lib/mapDirection'

export const DEFAULT_MAP_SETTINGS: MapSettings = {
  pin_shape: 'round',
  pin_colour: 'yellow',
  pin_size: 'medium',
  offline_mark: 'red_dot',
  show_number: true,
  map_style: 'hybrid',
  street_view: true,
  wedge_colour: 'red',   // #330 round 2
  wedge_fov_degrees: DEFAULT_FOV,   // #339 — 40
  wedge_range_m: DEFAULT_RANGE_M,   // #339 — 10
}

/** Light colours only: the number and the outline on the pin are dark and must read on all of them. */
export const PIN_COLOUR_HEX: Record<PinColour, string> = {
  yellow: '#fbbf24',   // amber-400, the brand colour
  green: '#4ade80',    // green-400
  blue: '#38bdf8',     // sky-400
  orange: '#fb923c',   // orange-400
  white: '#ffffff',
  grey: '#cbd5e1',     // slate-300
}

/** Pin width on screen; the height follows the shape's proportions. */
export const PIN_SIZE_WIDTH: Record<PinSize, number> = { small: 32, medium: 40, large: 48 }

/** red-500: the offline pin's fill with the "red pin" mark, and the red dot's colour. */
export const OFFLINE_RED = '#ef4444'

/** #330 round 2 (owner 2026-10-13) — the wedge colours that are not the pin colour. Bright red (red-500, the
 *  default) reads on the dark greens and shadows of satellite imagery, where the pin's yellow did not. */
export const WEDGE_COLOUR_HEX: Record<Exclude<WedgeColour, 'pin'>, string> = {
  red: '#ef4444',        // red-500
  white: '#ffffff',
  dark_red: '#7f1d1d',   // red-900
}

export const MAP_SETTINGS_CHOICES = {
  pin_shape: ['round', 'teardrop', 'dot', 'tag', 'lollipop', 'camera'] as PinShape[],
  pin_colour: ['yellow', 'green', 'blue', 'orange', 'white', 'grey'] as PinColour[],
  pin_size: ['small', 'medium', 'large'] as PinSize[],
  offline_mark: ['red_dot', 'red_pin', 'none'] as OfflineMark[],
  map_style: ['hybrid', 'satellite', 'roadmap', 'terrain'] as MapStyle[],
  wedge_colour: ['red', 'pin', 'white', 'dark_red'] as WedgeColour[],   // #330 round 2
}

export function withDefaults(partial?: Partial<MapSettings> | null): MapSettings {
  return { ...DEFAULT_MAP_SETTINGS, ...(partial ?? {}) }
}

/** The fill of a camera's pin: the chosen colour, or red for an offline camera when the offline
 *  mark is the whole pin. A camera with no status (FTP) is never red. */
export function pinFill(isOnline: boolean | null | undefined, settings: MapSettings): string {
  if (isOnline === false && settings.offline_mark === 'red_pin') return OFFLINE_RED
  return PIN_COLOUR_HEX[settings.pin_colour]
}

/** #330 round 2 — the colour a camera's direction wedge is drawn in: the chosen one, or the pin colour for 'pin'.
 *  A value this build does not know (a newer API) draws red. */
export function wedgeColour(settings: MapSettings): string {
  if (settings.wedge_colour === 'pin') return PIN_COLOUR_HEX[settings.pin_colour]
  return WEDGE_COLOUR_HEX[settings.wedge_colour] ?? WEDGE_COLOUR_HEX.red
}

/** #339 — the width and the range a camera with no width or range of its own is drawn with (the map wedges and the
 *  editors' compass), and the placeholders of the editors' empty fields. */
export function wedgeDefaults(settings: MapSettings): WedgeDefaults {
  return { fov: settings.wedge_fov_degrees, rangeM: settings.wedge_range_m }
}
